import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Shield, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "cashier", active: true });

  const load = async () => {
    try { setUsers((await api.get("/users")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "cashier", active: true }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, active: u.active }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          name: form.name, role: form.role, active: form.active,
          ...(form.password ? { password: form.password } : {}),
        });
      } else {
        if (!form.email.trim() || !form.password) return toast.error("Email & password wajib diisi");
        await api.post("/users", { name: form.name, email: form.email.toLowerCase(), password: form.password, role: form.role });
      }
      toast.success("Pengguna disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus pengguna "${u.name}"?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Pengguna dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <PageHeader title="Pengguna" subtitle={`${users.length} akun`}>
        <Button data-testid="add-user-btn" onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2">
          <Plus className="h-4 w-4" /> Tambah Pengguna
        </Button>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="user-auth-management">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F9FB] text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Nama</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Peran</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-slate-500">{u.email}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${u.role === "admin" ? "bg-indigo-500/15 text-indigo-400" : "bg-emerald-500/15 text-emerald-600"}`}>
                      {u.role === "admin" ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {u.role === "admin" ? "Admin" : "Kasir"}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`text-xs ${u.active ? "text-emerald-600" : "text-slate-500"}`}>{u.active ? "Aktif" : "Nonaktif"}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="h-8 w-8 rounded-lg bg-[#F7F9FB] hover:bg-slate-200 flex items-center justify-center"><Pencil className="h-4 w-4 text-slate-600" /></button>
                      {u.id !== me.id && (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="h-8 w-8 rounded-lg bg-[#F7F9FB] hover:bg-red-500/20 flex items-center justify-center"><Trash2 className="h-4 w-4 text-red-600" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog" className="bg-[#F7F9FB] border-slate-300 text-slate-800 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Nama</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#F7F9FB] border-slate-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Email</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#F7F9FB] border-slate-300 disabled:opacity-60" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">{editing ? "Password baru (opsional)" : "Password"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#F7F9FB] border-slate-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Peran</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-[#F7F9FB] border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                  <SelectItem value="cashier">Kasir</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center justify-between">
                <Label className="text-slate-600 text-sm">Akun Aktif</Label>
                <Switch data-testid="user-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB]">Batal</Button>
            <Button data-testid="save-user-btn" onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
